import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

interface TranslatableService {
  id: string;
  name: string;
  description: string | null;
}

interface TranslationCache {
  [lang: string]: {
    [id: string]: { name: string; description: string | null };
  };
}

/**
 * Translates service names/descriptions via AI when language is not PT.
 * Caches results in memory to avoid repeated calls.
 */
export const useTranslateServices = (services: TranslatableService[]) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const isPt = lang === "pt-PT" || lang === "pt";

  const cacheRef = useRef<TranslationCache>({});
  const [translated, setTranslated] = useState<TranslatableService[]>(services);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (isPt || services.length === 0) {
      setTranslated(services);
      return;
    }

    // Check cache
    const cached = cacheRef.current[lang];
    if (cached && services.every((s) => cached[s.id])) {
      setTranslated(
        services.map((s) => ({
          ...s,
          name: cached[s.id].name,
          description: cached[s.id].description,
        }))
      );
      return;
    }

    // Need translation
    const doTranslate = async () => {
      setTranslating(true);
      try {
        const texts = services.flatMap((s) => [
          { key: `name_${s.id}`, value: s.name },
          { key: `desc_${s.id}`, value: s.description || "" },
        ]);

        const { data, error } = await supabase.functions.invoke("translate", {
          body: { texts, targetLang: lang },
        });

        if (error || !data?.translated) {
          console.warn("Translation failed, using originals", error);
          setTranslated(services);
          return;
        }

        const map: Record<string, string> = {};
        (data.translated as { key: string; value: string }[]).forEach((t) => {
          map[t.key] = t.value;
        });

        // Build cache entry
        if (!cacheRef.current[lang]) cacheRef.current[lang] = {};
        const result = services.map((s) => {
          const tName = map[`name_${s.id}`] || s.name;
          const tDesc = map[`desc_${s.id}`] || s.description;
          cacheRef.current[lang][s.id] = { name: tName, description: tDesc };
          return { ...s, name: tName, description: tDesc };
        });

        setTranslated(result);
      } catch (e) {
        console.warn("Translation error:", e);
        setTranslated(services);
      } finally {
        setTranslating(false);
      }
    };

    doTranslate();
  }, [services, lang, isPt]);

  return { services: translated, translating };
};
